import { AssessmentResult, getLevelColor, formatDuration } from '../../types/assessment'

interface ResultsProps {
  result: AssessmentResult
  skillName?: string
  onRetry?: () => void
  onBack: () => void
}

export function Results({ result, skillName, onRetry, onBack }: ResultsProps) {
  // Calculate accuracy percentage
  const accuracyPercent = Math.round(result.accuracy * 100)

  // Get achievement message based on level
  const getAchievementMessage = () => {
    switch (result.level.toLowerCase()) {
      case 'expert':
        return 'Outstanding! You demonstrate expert-level mastery of this skill.'
      case 'advanced':
        return 'Excellent! You have advanced knowledge in this area.'
      case 'intermediate':
        return 'Good job! You have solid working knowledge of this skill.'
      case 'beginner':
        return "You're getting started. Keep learning and practicing!"
      default:
        return 'Assessment complete.'
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Success header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <svg
            className="w-8 h-8 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Assessment Complete!</h1>
        {skillName && <p className="text-gray-600 mt-1">{skillName}</p>}
      </div>

      {/* Level badge - prominent display */}
      <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
        <div className="text-center">
          <span
            className={`inline-block px-6 py-3 text-2xl font-bold text-white rounded-full ${getLevelColor(
              result.level
            )} shadow-lg`}
          >
            {result.level.charAt(0).toUpperCase() + result.level.slice(1)}
          </span>
          <p className="mt-4 text-gray-600">{result.level_description}</p>
          <p className="mt-2 text-lg font-medium text-gray-800">{getAchievementMessage()}</p>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {/* Percentile */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-3xl font-bold text-blue-600">{result.percentile}%</div>
          <div className="text-sm text-gray-600">Percentile Rank</div>
          <div className="text-xs text-gray-500 mt-1">
            Better than {result.percentile}% of test takers
          </div>
        </div>

        {/* Accuracy */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-3xl font-bold text-green-600">{accuracyPercent}%</div>
          <div className="text-sm text-gray-600">Accuracy</div>
          <div className="text-xs text-gray-500 mt-1">
            {result.correct_answers} of {result.questions_answered} correct
          </div>
        </div>

        {/* Questions answered */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-3xl font-bold text-purple-600">
            {result.questions_answered}
          </div>
          <div className="text-sm text-gray-600">Questions Answered</div>
          <div className="text-xs text-gray-500 mt-1">
            Adaptive assessment
          </div>
        </div>

        {/* Duration */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-3xl font-bold text-orange-600">
            {formatDuration(result.duration_seconds)}
          </div>
          <div className="text-sm text-gray-600">Time Taken</div>
          <div className="text-xs text-gray-500 mt-1">
            Total assessment duration
          </div>
        </div>
      </div>

      {/* Verification badge info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <svg
            className="w-6 h-6 text-blue-500 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
          <div>
            <h3 className="font-medium text-blue-900">Verification Badge Earned!</h3>
            <p className="text-sm text-blue-700 mt-1">
              Your {result.level} level badge is now visible on your profile. This verification
              is valid for 12 months.
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-4 justify-center">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
        >
          Back to Dashboard
        </button>
        {onRetry && (
          <button
            onClick={onRetry}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Take Another Assessment
          </button>
        )}
      </div>
    </div>
  )
}
